import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { PlanningPeriodKey } from '@crm/shared'
import {
  getAnalyticsSummary,
  getAnalyticsSummaryOptions,
  type AnalyticsPeriodParams,
  type SummaryFilterValues,
} from '../../../shared/api/analytics.api'
import { PlanningPeriodSelector } from '../../planning/PlanningPeriodSelector'
import { Spinner } from '../../../shared/ui/Spinner'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import { SummaryFilters } from './SummaryFilters'
import { SummaryKpi } from './SummaryKpi'
import { SummaryFunnel } from './SummaryFunnel'
import { SummaryDimensions } from './SummaryDimensions'
import { SummaryClientsTable, SummaryManagersTable } from './SummaryTables'
import { SummaryCompare } from './SummaryCompare'
import { periodLabel } from './summaryFormat'

/** Месяц, предшествующий текущему, в формате YYYY-MM (дефолт для периода B). */
function prevMonthKey(): string {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return d.toISOString().slice(0, 7)
}

export function SummaryTab({ periodParams }: { periodParams: AnalyticsPeriodParams }) {
  const [filters, setFilters] = useState<SummaryFilterValues>({})
  const [compare, setCompare] = useState(false)

  // Второй период (B) для режима сравнения — по умолчанию предыдущий месяц.
  const [periodKeyB, setPeriodKeyB] = useState<PlanningPeriodKey>('month')
  const [dateB, setDateB] = useState<string>(prevMonthKey())
  const [fromB, setFromB] = useState<string | null>(null)
  const [toB, setToB] = useState<string | null>(null)
  const periodParamsB: AnalyticsPeriodParams = {
    period: periodKeyB,
    date: periodKeyB === 'custom' ? null : dateB,
    from: periodKeyB === 'custom' ? fromB : null,
    to: periodKeyB === 'custom' ? toB : null,
  }

  const optionsQ = useQuery({
    queryKey: ['analytics-summary-options'],
    queryFn: getAnalyticsSummaryOptions,
  })

  const aQ = useQuery({
    queryKey: ['analytics-summary', periodParams, filters, compare],
    queryFn: () => getAnalyticsSummary(periodParams, filters, !compare),
  })

  const bQ = useQuery({
    queryKey: ['analytics-summary-b', periodParamsB, filters],
    queryFn: () => getAnalyticsSummary(periodParamsB, filters, false),
    enabled: compare,
  })

  const labelA = periodLabel(periodParams)
  const labelB = periodLabel(periodParamsB)

  return (
    <div className="space-y-4">
      {/* Управление: фильтры + переключатель режима + селектор периода B */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <SummaryFilters options={optionsQ.data} value={filters} onChange={setFilters} />
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex rounded-xl border border-gray-200 bg-white overflow-hidden">
            <button
              onClick={() => setCompare(false)}
              className={`px-3 py-1.5 text-sm transition-colors ${
                !compare ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Период
            </button>
            <button
              onClick={() => setCompare(true)}
              className={`px-3 py-1.5 text-sm transition-colors border-l border-gray-200 ${
                compare ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Сравнить
            </button>
          </div>
          {compare && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">с</span>
              <PlanningPeriodSelector
                periodKey={periodKeyB}
                date={dateB}
                from={fromB}
                to={toB}
                onPeriodChange={setPeriodKeyB}
                onDateChange={setDateB}
                onCustomChange={(f, t) => {
                  setFromB(f)
                  setToB(t)
                }}
              />
            </div>
          )}
        </div>
      </div>

      {aQ.isLoading || (compare && bQ.isLoading) ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : aQ.isError || !aQ.data ? (
        <ErrorMessage error="Не удалось загрузить сводку" />
      ) : compare ? (
        bQ.data ? (
          <>
            <SummaryCompare a={aQ.data} b={bQ.data} labelA={labelA} labelB={labelB} />
            <div className="pt-2">
              <div className="text-xs text-gray-500 mb-2">Детализация по периоду «{labelA}»:</div>
              <SummaryDimensions data={aQ.data} />
            </div>
          </>
        ) : (
          <ErrorMessage error="Не удалось загрузить второй период" />
        )
      ) : (
        <>
          <SummaryKpi data={aQ.data} periodParams={periodParams} filters={filters} />
          <SummaryFunnel data={aQ.data} />
          <SummaryDimensions data={aQ.data} />
          <div className="grid gap-4">
            <SummaryClientsTable data={aQ.data} />
            <SummaryManagersTable data={aQ.data} />
          </div>
        </>
      )}
    </div>
  )
}
